import { createAsyncThunk } from '@reduxjs/toolkit';
import { Estimate, Id, RateAsPayorMap } from '@finpay/estimation-types';
import { admissionsAdvisorService } from '../../admissions-advisor/services/admissions-advisor-services';
import { patientHelper } from '../../patient/services/patient-service';
import { setSavedEstimatesList, setSavedEstimatesListLoading, setSavedEstimatesListError, setRateAsPayorMap } from './estimator-slice';

// Load selected estimate by ID
export const loadSelectedEstimate = createAsyncThunk(
  'estimatorContext/loadSelectedEstimate',
  async (config: { paramId: number; estimationId: number | string }, thunkAPI) => {
    const response = await admissionsAdvisorService.callV3EstimateGet(
      config.paramId,
      Number(config.estimationId)
    );
    if (response.hasErrors) {
      patientHelper.robustErrorHandler(response, thunkAPI);
    } else {
      return response.entity;
    }
  }
);

// Save/update selected estimate
export const saveSelectedEstimate = createAsyncThunk(
  'estimatorContext/saveSelectedEstimate',
  async (config: { estimate: Estimate; estimateId?: Id }, thunkAPI) => {
    const response = await admissionsAdvisorService.callV3EstimatePut(
      config.estimate,
      config.estimateId
    );

    if (response.hasErrors) {
      patientHelper.robustErrorHandler({
        entity: { code: "", message: "Failed to update estimate. Please contact FinPay Support" },
        errorMessage: "Failed to update estimate. Please contact FinPay Support",
        hasErrors: true
      }, thunkAPI);
    }

    return response.entity;
  }
);

export const saveSelectedEstimateSelfPay = createAsyncThunk(
  'estimatorContext/saveSelectedEstimate',
  async (config: { estimate: Estimate; estimateId?: Id }, thunkAPI) => {
    const response = await admissionsAdvisorService.callV3EstimateSelfPayPut(
      config.estimate,
      config.estimateId
    );

    if (response.hasErrors) {
      patientHelper.robustErrorHandler({
        entity: { code: "", message: "Failed to update estimate. Please contact FinPay Support" },
        errorMessage: "Failed to update estimate. Please contact FinPay Support",
        hasErrors: true
      }, thunkAPI);
    }

    return response.entity;
  }
);

// Create new estimate
export const createSelectedEstimate = createAsyncThunk(
  'estimatorContext/createSelectedEstimate',
  async (estimate: Estimate, thunkAPI) => {
    const response = await admissionsAdvisorService.callV3EstimatePost(estimate);

    if (response.hasErrors) {
      patientHelper.robustErrorHandler({
        entity: { code: "", message: "Failed to create estimate. Please contact FinPay Support" },
        errorMessage: "Failed to create estimate. Please contact FinPay Support",
        hasErrors: true
      }, thunkAPI);
    }

    return response.entity;
  }
); 

// Create new estimate
export const createSelectedEstimateSelfPay = createAsyncThunk(
  'estimatorContext/createSelectedEstimate',
  async (estimate: Estimate, thunkAPI) => {
    const response = await admissionsAdvisorService.callV3EstimateSelfPayPost(estimate);

    if (response.hasErrors) {
      patientHelper.robustErrorHandler({
        entity: { code: "", message: "Failed to create estimate. Please contact FinPay Support" },
        errorMessage: "Failed to create estimate. Please contact FinPay Support",
        hasErrors: true
      }, thunkAPI);
    }

    return response.entity;
  }
); 

export const getRateAsPayorMap = createAsyncThunk(
  'estimatorContext/getRateAsPayorMap',
  async (config: { paramId: number; clientId?: number; facilityId?: number }, { dispatch, rejectWithValue }) => {
    try {
      const response = await admissionsAdvisorService.getRateAsPayorMap(
        config.paramId,
        config.clientId,
        config.facilityId
      );

      if (response.hasErrors) {
        const errorMessage = response.errorMessage || 'Failed to load rate as payor map';
        console.error('Error loading rate as payor map:', errorMessage);
        return rejectWithValue(errorMessage);
      }

      // Get the raw array data from the response
      const rateAsPayorMapData = response.entity as RateAsPayorMap[];

      // Dispatch the array to the reducer which will convert it to a map
      dispatch(setRateAsPayorMap(rateAsPayorMapData));

      return rateAsPayorMapData;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load rate as payor map';
      console.error('Error loading rate as payor map:', error);
      return rejectWithValue(errorMessage);
    }
  }
);

export const loadSavedEstimatesListv3 = createAsyncThunk(
  'estimatorContext/loadSavedEstimatesListv3',
  async (config: { paramId: number; patientId: number }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setSavedEstimatesListLoading(true));
      
      const response = await admissionsAdvisorService.getEstimationsv3({
        paramId: config.paramId,
        queryStringParams: {
          patientId: config.patientId
        }
      });

      if (response.hasErrors) {
        const errorMessage = response.errorMessage || 'Failed to load saved estimates';
        dispatch(setSavedEstimatesListError(errorMessage));
        patientHelper.robustErrorHandler(response, { rejectWithValue });
        return rejectWithValue(errorMessage);
      }

      // Update the new estimator context
      dispatch(setSavedEstimatesList(response.entity));
      dispatch(setSavedEstimatesListLoading(false));

      return response.entity;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load saved estimates';
      dispatch(setSavedEstimatesListError(errorMessage));
      dispatch(setSavedEstimatesListLoading(false));
      console.error('Error loading saved estimates:', error);
      return rejectWithValue(errorMessage);
    }
  }
);

export const saveEstimateAfterVob = createAsyncThunk(
  'estimatorContext/saveEstimateAfterVob',
  async (config: { 
    estimate: Estimate; 
    estimateId?: Id; 
    isSelfPay?: boolean; 
    isPrivatePay?: boolean; 
  }, { dispatch }) => {
    const { estimate, estimateId, isSelfPay, isPrivatePay } = config;

    const saveAction = (isSelfPay || isPrivatePay) 
      ? saveSelectedEstimateSelfPay 
      : saveSelectedEstimate;
    
    const result = await dispatch(saveAction({
      estimate,
      estimateId
    }));
    
    return result.payload;
  }
);